<template>
    <div>
        <breadcrumb :items="breadcrumbs"></breadcrumb>
        <v-container fluid class="goal-details-container">
            <div class="d-flex justify-space-between align-center flex-wrap mb-6">
                <h2 class="page-title">{{ $t("esgPlatform.surveyOverviewPage.goalDetails") }}</h2>
                <v-btn color="primary" class="text-capitalize" @click="goBack">{{ $t("esgPlatform.esgReport.back") }}</v-btn>
            </div>

            <v-card flat class="goal-meta-card pa-6 mb-6">
                    <div class="meta-row" >
                        <span class="meta-label" style="min-width: 100px;">{{ $t("esgPlatform.goal") }}:</span>
                        <span class="meta-value">{{ goalTitle }}</span>
                    </div>
                <v-row dense>
                    <v-col cols="12" md="3">
                        <div class="meta-row">
                            <span class="meta-label" style="min-width: 140px;">{{ $t("esgPlatform.surveyOverviewPage.totalQuestions") }}:</span>
                            <span class="meta-value">{{ totalQuestions }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label" style="min-width: 120px;">{{ $t("esgPlatform.surveyOverviewPage.targetYear") }}:</span>
                            <span class="meta-value">{{ goalTargetYear }}</span>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex flex-column justify-center">
                        <div class="d-flex align-center justify mb-3">
                            <span class="meta-label" style="min-width: 100px;">{{ $t("esgPlatform.progress.progress") }}:</span>
                            <v-progress-linear
                            :value="progressPercentage"
                            :color="progressColor"
                            rounded
                            height="10"
                            background-color="#E6E9EE"
                            class="mr-3"
                            />
                            <span class="progress-value" >{{ progressPercentage }}%</span>
                        </div>
                        <div class="d-flex align-center">
                            <span class="meta-label mr-3">{{ $t("esgPlatform.status") }}:</span>
                            <span :class="['goal-status-badge', goalStatusClass]">{{ goalStatusLabel }}</span>
                        </div>
                    </v-col>
                </v-row>
            </v-card>

            <v-expansion-panels flat accordion class="mb-6">
                <v-expansion-panel>
                    <v-expansion-panel-header class="section-header">{{ $t("esgPlatform.description") }}:</v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div class="section-content" v-html="survey?.description || '-'"></div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels flat accordion class="mb-6">
                <v-expansion-panel>
                    <v-expansion-panel-header class="section-header">{{ $t("esgPlatform.surveyInstructions") }}:</v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div class="section-content" v-html="survey?.instructions || '-'"></div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <v-card flat class="questions-section pa-6">
                <div class="section-title mb-3">{{ $t("esgPlatform.questions") }}:</div>

                <v-row dense class="mb-3 questions-filter-row">
                    <v-col cols="12" md="2">
                        <div class="filter-label placeholder-label">&nbsp;</div>
                        <v-text-field
                            v-model="searchKeyword"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                            :placeholder="$t('esgPlatform.progress.search')"/>
                    </v-col>

                    <v-col cols="12" md="2" lg="2">
                        <div class="filter-label">
                            <span>{{ $t("esgPlatform.type") }}</span>
                            <v-icon x-small color="#00BD73">mdi-information</v-icon>
                        </div>
                        <v-select
                            v-model="selectedType"
                            :items="typeFilterOptions"
                            item-text="label"
                            item-value="value"
                            outlined
                            dense
                            hide-details
                            clearable
                            :placeholder="$t('esgPlatform.progress.select')"
                        />
                    </v-col>

                    <v-col cols="12" md="4" lg="2">
                        <div class="filter-label">
                            <span>{{ $t("esgPlatform.status") }}</span>
                            <v-icon x-small color="#00BD73">mdi-information</v-icon>
                        </div>
                        <v-select
                            v-model="selectedStatus"
                            :items="statusFilterOptions"
                            item-text="label"
                            item-value="value"
                            outlined
                            dense
                            hide-details
                            clearable
                            :placeholder="$t('esgPlatform.progress.select')"
                        />
                    </v-col>

                    <v-col cols="12" lg="1" class="d-flex align-end">
                        <v-checkbox
                            v-model="showOnlyRepeatSubmissions"
                            hide-details
                            class="repeat-only-checkbox ma-0 pa-0"
                            :label="$t('esgPlatform.surveyOverviewPage.showOnlyRepeatSubmissions')"
                        />
                    </v-col>
                </v-row>

                   <div class="questions-table-head">
                    <div class="head-question">{{ $t("esgPlatform.questions") }}</div>
                    <div class="head-type">{{ $t("esgPlatform.type") }}</div>
                    <div class="head-status">{{ $t("esgPlatform.status") }}</div>
                    <div class="head-view"></div>
                </div>

                <div v-for="group in filteredQuestionGroups" :key="group.headingId" class="question-group mb-3">
                    <div class="group-header" @click="toggleGroup(group.headingId)">
                        <div class="group-header-left">
                            <span class="group-index-badge">{{ group.groupNumber }}</span>
                            <span class="group-title">{{ group.title }}</span>
                        </div>
                        <v-icon color="#6f6f6f" size="20">
                            {{ isGroupExpanded(group.headingId) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        </v-icon>
                    </div>

                    <div v-show="isGroupExpanded(group.headingId)" class="group-body">
                        <div
                            v-for="row in group.questions"
                            :key="row.id"
                            :class="['question-row', getQuestionRowClass(row.status)]"
                        >
                            <div class="row-question">
                                <span :class="['question-number-tag', getQuestionTagClass(row.status)]">{{ row.questionNumber }}</span>
                                <span class="question-title">{{ row.title }}</span>
                            </div>

                            <div class="row-type">
                                <div class="type-value">{{ formatQuestionType(row.questionType) }}</div>
                                <div v-if="row.isRepeatSubmission" class="row-repeat-submission">
                                    <v-icon x-small color="#00BD73">mdi-check-circle</v-icon>
                                    <span>{{ $t("esgPlatform.surveyResponse.repeatSubmission") }}</span>
                                </div>
                            </div>

                            <div class="row-status">
                                <span :class="['question-status-badge', `status-${row.status}`]">{{ formatStatus(row.status) }}</span>
                            </div>

                            <div class="row-view">
                                <v-btn class="view-action-btn" icon x-small @click.stop="viewQuestion(row)">
                                    <v-icon size="18" color="#00BD73">mdi-eye-outline</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </div>
                <v-alert v-if="!filteredQuestionGroups.length" outlined type="warning" class="mb-0 mt-2">
                    {{ $t("esgPlatform.surveyOverviewPage.noQuestionsFound") }}
                </v-alert>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import EsgService from '@/_services/EsgService';

export default {
    name: 'SurveyOverview',
    mixins: [loadingMixin],
    data() {
        return {
            survey: null,
            surveyId: this.$route.params.id1,
            goalId: this.$route.params.id2,
            goalData: null,
            questionGroups: [],
            scoreInfo: {
                progress: 0,
                totalQuestionScore: 0,
                totalResponseScore: 0,
                assessmentId: "",
                goalId: "",
                status: "not-started",
            },
            searchKeyword: "",
            selectedType: null,
            selectedStatus: null,
            showOnlyRepeatSubmissions: false,
            expandedGroupIds: [],
            breadcrumbs: [
                {
                    text: this.$t("esgPlatform.esgPlatform"),
                    link: true,
                    exact: true,
                    disabled: true,
                },
                {
                    text: this.$t("dashboard"),
                    disabled: false,
                    to: { name: "eudrDashboard" },
                },
            ],
        };
    },
    computed: {
        goalTitle() {
            return this.goalData?.title || "-";
        },
        goalTargetYear() {
            const year = this.goalData?.targetYears;
            if (Array.isArray(year)) {
                return year.join(", ");
            }
            return year ?? "-";
        },
        totalQuestions() {
            return this.goalData?.noOfQuestions ?? "-";
        },
        progressPercentage() {
            return Number(this.scoreInfo?.progress || 0);
        },
        progressColor() {
            const progress = this.progressPercentage;
            if (progress <= 24) return "#F03737";
            if (progress <= 74) return "#FFB443";
            return "#00BD73";
        },
        goalStatusLabel() {
            return this.formatStatus(this.scoreInfo?.status);
        },
        goalStatusClass() {
            const status = String(this.scoreInfo?.status || "").toLowerCase();
            if (status.includes("reject")) return "status-rejected";
            if (status.includes("submit") || status.includes("approve") || status.includes("complete")) {
                return "status-submitted";
            }
            return "status-pending";
        },
        typeFilterOptions() {
            const uniqueTypes = new Set();
            this.questionGroups.forEach((group) => {
                group.questions.forEach((question) => {
                    if (question.questionType) {
                        uniqueTypes.add(question.questionType);
                    }
                });
            });

            return Array.from(uniqueTypes)
                .sort()
                .map((type) => ({
                    label: this.formatQuestionType(type),
                    value: type,
                }));
        },
        statusFilterOptions() {
            return [
                { label: this.formatStatus("submitted"), value: "submitted" },
                { label: this.formatStatus("rejected"), value: "rejected" },
                { label: this.formatStatus("pending"), value: "pending" },
            ];
        },
        filteredQuestionGroups() {
            const search = this.searchKeyword.trim().toLowerCase();

            return this.questionGroups
                .map((group) => {
                    const filteredQuestions = group.questions.filter((question) => {
                        const matchesSearch = !search ||
                            `${question.questionNumber} ${question.title}`.toLowerCase().includes(search);
                        const matchesType = !this.selectedType || question.questionType === this.selectedType;
                        const matchesStatus = !this.selectedStatus || question.status === this.selectedStatus;
                        const matchesRepeat = !this.showOnlyRepeatSubmissions || question.isRepeatSubmission;

                        return matchesSearch && matchesType && matchesStatus && matchesRepeat;
                    });

                    return {
                        ...group,
                        questions: filteredQuestions,
                    };
                })
                .filter((group) => group.questions.length > 0);
        },
    },
    async mounted() {
        await this.loadGoalDetails();
    },
    methods: {
        async loadGoalDetails() {
            this.startLoading();
            try {
                const query = this.$route.query;

                if (query.progress !== undefined) {
                    this.scoreInfo.progress = Number(query.progress) || 0;
                }
                if (query.status) {
                    this.scoreInfo.status = query.status;
                }

                const [assessmentResponse, response] = await Promise.all([
                    EsgService.getAssessmentById(this.surveyId),
                    EsgService.getSurveyResponsesByAssessmentAndGoal(
                        this.surveyId,
                        this.goalId,
                        {
                            submittedById: query.subOrganizationId,
                        }
                    ),
                ]);
                this.survey = assessmentResponse?.data || null;
                this.goalData = response?.data?.goaldata || null;
                if (this.goalData?._id) {
                    this.goalId = this.goalData._id;
                }

                const headings = Array.isArray(response?.data.assessmentQuestion) ? response.data.assessmentQuestion : [];
                this.questionGroups = this.mapQuestionGroups(headings);
                this.expandedGroupIds = this.questionGroups.map((group) => group.headingId);

                const topLevelScoreInfo = response?.score_information;
                const nestedScoreInfo = headings.find((heading) => heading?.score_information)?.score_information;
                if (topLevelScoreInfo || nestedScoreInfo) {
                    this.scoreInfo = {
                        ...this.scoreInfo,
                        ...(topLevelScoreInfo || nestedScoreInfo),
                    };
                }
            } catch (error) {
                console.error("Error loading goal details:", error);
                this.$notify({
                    type: "error",
                    text: this.$t("esgPlatform.errorLoadingSurvey"),
                });
            } finally {
                this.stopLoading();
            }
        },
        mapQuestionGroups(headings = []) {
            const sortedHeadings = [...headings].sort((a, b) => (a?.order || 0) - (b?.order || 0));

            return sortedHeadings
                .map((heading, headingIndex) => {
                    const sortedQuestions = [...(heading?.assessmentQuestions || [])]
                        .filter((question) => !question?.deletedAt)
                        .sort((a, b) => (a?.order || 0) - (b?.order || 0));

                    const questions = sortedQuestions.map((question, questionIndex) => {
                        const title = question?.title || question?.question || "-";
                        const questionType = question?.questionType || question?.type || "";
                        return {
                            id: question?._id || `${heading?._id}-${questionIndex}`,
                            headingId: heading?._id || `heading-${headingIndex + 1}`,
                            questionNumber: `${headingIndex + 1}.${questionIndex + 1}`,
                            title,
                            questionType,
                            status: this.getQuestionStatus(question),
                            isRepeatSubmission: this.isRepeatSubmission(question),
                        };
                    });

                    return {
                        headingId: heading?._id || `heading-${headingIndex + 1}`,
                        groupNumber: headingIndex + 1,
                        title: heading?.title || this.$t("esgPlatform.surveyOverviewPage.fallbackCategory", {
                            number: headingIndex + 1,
                        }),
                        questions,
                    };
                })
                .filter((group) => group.questions.length);
        },
        getQuestionStatus(question) {
            let hasRejectionReason = false;

            if (Array.isArray(question?.response) && question.response.length > 0) {
            const hasRepetitionDate = question.response.some(r => r?.repetitionDate);
            if (hasRepetitionDate) {
                const latest = question.response.reduce((prev, curr) =>
                    new Date(curr.repetitionDate) > new Date(prev.repetitionDate) ? curr : prev
                );
                hasRejectionReason = latest?.rejectionReason != null;
            } else {
                hasRejectionReason = question?.rejectionReason != null;
            }
            } else {
                hasRejectionReason = question?.rejectionReason != null;
            }
            const response = question?.response;
            const hasResponse =
            response != null &&
            (
                (Array.isArray(response) && response.length > 0) ||
                (typeof response === 'object' && !Array.isArray(response) && Object.keys(response).length > 0)
            );

            if (hasRejectionReason) return "rejected";
            if (hasResponse) return "submitted";
            return "pending";
        },
        isRepeatSubmission(question) {
            return Boolean(
                question?.isRepeatEnabled ||
                (question?.repetitionCount || 0) > 0 ||
                (Array.isArray(question?.repetitionDates) && question.repetitionDates.length > 0)
            );
        },
        formatQuestionType(type) {
            if (!type) return "-";
            const normalizedType = String(type).toLowerCase().replace(/_/g, "-");
            const typeKeyMap = {
                "text-field": "textField",
                "text-area": "textArea",
                numeric: "textFieldNumeric",
                "radio-button": "radioButton",
                "check-boxes": "checkBoxes",
                "attach-files": "attachFiles",
                "digital-signature": "digitalSignature",
                "get-data": "getDataLabel",
                date: "date",
            };
            const translationKey = typeKeyMap[normalizedType];
            if (translationKey) {
                return this.$t(`esgPlatform.${translationKey}`);
            }
            return this.formatPlainLabel(type);
        },
        formatStatus(status) {
            if (!status) return "-";
            const normalizedStatus = String(status).toLowerCase().replace(/_/g, "-");
            const statusKeyMap = {
                submitted: "submitted",
                rejected: "rejected",
                pending: "pending",
                approved: "approved",
                completed: "completed",
                "not-started": "notStarted",
                "in-progress": "inProgress",
            };
            const translationKey = statusKeyMap[normalizedStatus];
            if (translationKey) {
                return this.$t(`esgPlatform.${translationKey}`);
            }
            return this.formatPlainLabel(status);
        },
        formatPlainLabel(value) {
            return String(value)
                .split(/[-_]/)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
        capitalizeFirst(value) {
            if (!value) return "-";
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
        isGroupExpanded(headingId) {
            return this.expandedGroupIds.includes(headingId);
        },
        toggleGroup(headingId) {
            if (this.isGroupExpanded(headingId)) {
                this.expandedGroupIds = this.expandedGroupIds.filter((id) => id !== headingId);
                return;
            }
            this.expandedGroupIds = [...this.expandedGroupIds, headingId];
        },
        getQuestionRowClass(status) {
            if (status === "submitted") return "row-submitted";
            if (status === "rejected") return "row-rejected";
            return "row-pending";
        },
        getQuestionTagClass(status) {
            if (status === "submitted") return "tag-submitted";
            if (status === "rejected") return "tag-rejected";
            return "tag-pending";
        },
        viewQuestion(question) {
            this.$router.push({
                name: "surveyOverviewResponse",
                params: { id1: this.surveyId, id2: this.goalId },
                query: {
                    ...this.$route.query,
                    headingId: question.headingId,
                    questionId: question.id,
                },
            });
        },
        goBack() {
            this.$router.go(-1);
        },
    },
};
</script>

<style lang="scss" scoped>
.goal-details-container {
    padding-bottom: 24px;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
}

.goal-meta-card,
.questions-section {
    background: #ffffff;
    border: 1px solid #e4e8ef;
    border-radius: 12px;
}

.meta-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
}

.meta-label {
    font-size: 16px;
    font-weight: 600;
    color: #000000;
}

.meta-value {
    font-size: 15px;
    color: #1d2939;
}

.progress-value {
    font-size: 16px;
    font-weight: 700;
    color: #1d2939;
}

.goal-status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 80px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    text-transform: capitalize;
    border: 1px solid transparent;
}

.goal-status-badge.status-submitted {
    background-color: #e7f7ef;
    color: #00a25b;
    border: 1px solid #00a25b;
}

.goal-status-badge.status-rejected {
    background-color: #ffe8e8;
    color: #d92d20;
    border: 1px solid #d92d20;
}

.goal-status-badge.status-pending {
    background-color: #eef1f5;
    color: #667085;
    border: 1px solid #667085;
}

.section-header {
    font-size: 16px;
    font-weight: 700;
}

.section-content {
    color: #344054;
    line-height: 1.6;
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #101828;
}

.questions-section {
    background: #f9fafb;
}

.questions-filter-row ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 6px !important;
}

.filter-label {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 20px;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 700;
    color: #4d5761;
}

.placeholder-label {
    visibility: hidden;
}

.repeat-only-checkbox ::v-deep .v-label {
    font-size: 13px;
    color: #3f3f46;
    white-space: nowrap;
}

.questions-table-head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 220px 180px 68px;
    align-items: center;
    gap: 120px;
    background: #1f7f4c;
    color: #ffffff;
    border-radius: 4px;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 10px;
}

.question-group {
    overflow: visible;
}

.group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #d7d8db;
    border-radius: 4px;
    padding: 8px 14px;
    cursor: pointer;
}

.group-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.group-index-badge {
    min-width: 28px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #8e8f91;
    color: #ffffff;
    font-size: 15px;
    font-weight: 700;
}

.group-title {
    color: #3f3f46;
    font-size: 16px;
    font-weight: 900;
}

.group-body {
    padding-top: 8px;
}

.question-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 220px 180px 68px;
    align-items: center;
    gap: 160px;
    background: #ffffff;
    border: 1px solid #d0d5dd;
    border-radius: 4px;
    margin-bottom: 8px;
    padding: 9px 14px;
}

.row-submitted {
    border-color: #75d9a9;
}

.row-rejected {
    border-color: #ffb7bb;
}

.row-pending {
    border-color: #cfd4dc;
}

.row-question {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.question-number-tag {
    min-width: 30px;
    height: 28px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
}

.tag-submitted {
    background: #06c270;
}

.tag-rejected {
    background: #ff4d4f;
}

.tag-pending {
    background: #9b9da3;
}

.question-title {
    color: #03050a;
    font-weight:500;
    font-size: 15px;
    line-height: 1.35;
}

.type-value {
    color: #2c3138;
    font-weight: 500;
    font-size: 16px;
    text-align: center;
}

.row-repeat-submission {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
    color: #667085;
    font-size: 12px;
}

.question-status-badge {
    min-width: 150px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 6px 20px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 16px;
    font-weight: 600;
    text-transform: capitalize;
}

.question-status-badge.status-submitted {
    background: #dcfce7;
    border-color: #55d79e;
    color: #16a34a;
}

.question-status-badge.status-rejected {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
}

.question-status-badge.status-pending {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #4b5563;
}

.row-view {
    display: flex;
    justify-content: center;
}

.view-action-btn {
    width: 32px;
    height: 32px;
    border: 2px solid #75d9a9;
    border-radius: 50%;
}

.view-action-btn::before {
    opacity: 0;
}

@media (max-width: 960px) {
    .meta-label {
        min-width: 120px;
    }

    .page-title {
        font-size: 24px;
    }

    .questions-table-head {
        display: none;
    }

    .group-title {
        font-size: 18px;
    }

    .question-row {
        grid-template-columns: 1fr;
        gap: 8px;
        padding: 10px 12px;
    }

    .row-status,
    .row-view {
        justify-content: flex-start;
    }

    .question-title {
        font-size: 14px;
    }

    .repeat-only-checkbox ::v-deep .v-label {
        white-space: normal;
    }
}
</style>
